// positionSync.js - positions-20261017
//
// Feeds the position tags from the chain:
//   - OPEN/CLOSED + SIZE: one shared balance poll for every mint (summed token
//     accounts, both token programs). Closed = summed balance 0 on two reads
//     in a row. A failed RPC read changes nothing.
//   - ENTRY: average cost since the wallet was last flat, from the wallet's own
//     fills - backfilled forward from chain history, kept fresh by
//     wallet_activity (deduped by signature). Stored as SOL per token; the
//     chart turns it into mcap with the real supply at draw time.
// Poll triggers: 15 s timer, trades, reload (setWallet), WS reconnect, coin
// switch (want), window online / tab visible.
// Records live in positionStore (wallet:mint keyed), so every reader stays the same.
import { PublicKey } from '@solana/web3.js'
import { positionStore } from './positionStore'
import { getWalletMintTotals, getMintFillsSinceFlat } from '../../utils/jupiter'

const POLL_MS = 15000
const CONFIRM_MS = 2500        // second read to confirm a zero
const PENDING_MS = 90000       // a fresh buy may read 0 until the RPC catches up
const LIVE_KEEP_MS = 120000    // live fills the chain hasn't indexed yet
const MAX_FILLS = 200
const DEFAULT_SUPPLY = 1_000_000_000

let walletPk = null
let wallet58 = ''
let cfg = { getSupply: () => 0, getFallbackPriceSol: () => 0, onBalance: () => {} }
const wanted = new Set()
let pollTimer = 0
let soonTimer = 0, soonAt = 0
let inFlight = false, again = false
let lastTotals = null
let listenersOn = false
const bfQueue = []
const bfNext = new Map()       // mint -> earliest next backfill time
const bfMiss = new Map()       // mint -> backfills that found no entry
let bfRunning = false

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0 }
const sig16 = (s) => String(s || '').slice(0, 16)
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`

function supplyFor(mint, rec) {
  let s = 0
  try { s = num(cfg.getSupply(mint)) } catch {}
  if (s > 0) return s
  if (rec && num(rec.tokenSupply) > 0) return num(rec.tokenSupply)
  return DEFAULT_SUPPLY
}

// One record per mint; an old multi-entry list collapses to a weighted average.
function readRec(mint) {
  const list = positionStore.get(mint)
  if (!list.length) return null
  if (list.length === 1) return { ...list[0] }
  // pos-tag-20261023: blend the SOL price too. It used to keep list[0]'s
  // entryPriceSol, which every reader prefers, so the average was lost.
  let tok = 0, w = 0, wp = 0
  for (const p of list) { const t = num(p.tokenAmount); tok += t; w += num(p.entryMcap) * t; wp += priceSolOf(p) * t }
  return { ...list[0], tokenAmount: tok,
    entryMcap: tok > 0 ? w / tok : num(list[0].entryMcap),
    entryPriceSol: tok > 0 && wp > 0 ? wp / tok : priceSolOf(list[0]) }
}

function priceSolOf(rec) {
  if (!rec) return 0
  if (num(rec.entryPriceSol) > 0) return num(rec.entryPriceSol)
  const s = num(rec.tokenSupply) > 0 ? num(rec.tokenSupply) : DEFAULT_SUPPLY
  return num(rec.entryMcap) > 0 ? num(rec.entryMcap) / s : 0
}

function writeRec(mint, rec) {
  const supply = supplyFor(mint, rec)
  const ps = num(rec.entryPriceSol) > 0 ? num(rec.entryPriceSol) : priceSolOf(rec)
  positionStore.set(mint, [{
    ...rec,
    token: mint,
    tokenSupply: supply,
    entryPriceSol: ps,
    entryMcap: ps > 0 ? ps * supply : num(rec.entryMcap),
    fills: Array.isArray(rec.fills) ? rec.fills.slice(-MAX_FILLS) : [],
  }])
}

// Average cost since flat. A sell takes cost out pro rata (average unchanged);
// a buy with no SOL leg (transfer in) adds tokens at the current average.
export function computeEntry(fills) {
  let qty = 0, cost = 0
  for (const f of fills || []) {
    const t = num(f && f.tok)
    if (!(t > 0)) continue
    if (f.side === 'buy') {
      const s = num(f.sol)
      if (s > 0) cost += s
      else if (qty > 0) cost += (cost / qty) * t
      qty += t
    } else if (f.side === 'sell') {
      if (qty > 0) cost -= cost * Math.min(1, t / qty)
      qty = Math.max(0, qty - t)
      if (qty <= 1e-12) { qty = 0; cost = 0 }
    }
  }
  return { qty, priceSol: qty > 0 && cost > 0 ? cost / qty : 0 }
}

function pushFill(mint, fill) {
  if (!mint || !fill || !(num(fill.tok) > 0)) return false
  const key = sig16(fill.sig)
  const f = { ...fill, sig: key }
  const rec = readRec(mint)
  if (rec) {
    const fills = Array.isArray(rec.fills) ? rec.fills : []
    if (key && fills.some((x) => sig16(x && x.sig) === key)) return false   // already counted
    let base = fills
    // Known position with no fill list yet (pre-20261017 / detected): seed it
    // so the new fill averages in instead of replacing the history.
    if (!fills.length && rec.source !== 'instant' && rec.source !== 'tape'
        && priceSolOf(rec) > 0 && num(rec.tokenAmount) > 0) {
      base = [{ sig: 'seed', ts: num(rec.timestamp), side: 'buy', tok: num(rec.tokenAmount), sol: num(rec.tokenAmount) * priceSolOf(rec) }]
    }
    const next = base.concat(f)
    const { priceSol } = computeEntry(next)
    writeRec(mint, {
      ...rec,
      fills: next,
      entryPriceSol: priceSol > 0 ? priceSol : priceSolOf(rec),
      detected: priceSol > 0 ? false : !!rec.detected,
      source: rec.source === 'instant' ? 'tape' : rec.source,
    })
    return true
  }
  if (f.side !== 'buy' || !(num(f.sol) > 0)) return false
  writeRec(mint, {
    id: newId(), wallet: wallet58, token: mint,
    tokenAmount: num(f.tok), entryPriceSol: num(f.sol) / num(f.tok),
    fills: [f], timestamp: Date.now(), pendingUntil: Date.now() + PENDING_MS,
    backfilledAt: 0, zeroReads: 0, source: 'tape', entrySolPrice: 0,
  })
  return true
}

function applyTotals(totals) {
  const now = Date.now()
  const mints = new Set([...positionStore.mints(), ...wanted])
  let confirm = false
  for (const mint of mints) {
    const t = totals.get(mint)
    const rec = readRec(mint)
    if (!t || !t.nonzero) {
      try { cfg.onBalance(mint, 0) } catch {}
      if (!rec) continue
      if (num(rec.pendingUntil) > now) continue
      const zr = num(rec.zeroReads) + 1
      if (zr >= 2) positionStore.set(mint, [])
      else { writeRec(mint, { ...rec, zeroReads: zr }); confirm = true }
      continue
    }
    const bal = t.ui
    try { cfg.onBalance(mint, bal) } catch {}
    if (!rec) {
      if (wanted.has(mint)) queueBackfill(mint)   // creates the record once it has an entry
      continue
    }
    if (num(rec.tokenAmount) !== bal || num(rec.zeroReads) || num(rec.pendingUntil)) {
      writeRec(mint, { ...rec, tokenAmount: bal, zeroReads: 0, pendingUntil: 0 })
    }
    if (wanted.has(mint)) {
      const fills = Array.isArray(rec.fills) ? rec.fills : []
      const never = !num(rec.backfilledAt)
      // Fills don't explain the balance (missed trade, transfer): re-backfill,
      // but only once per balance value so a short history can't loop.
      const mismatch = fills.length > 0 && num(rec.backfillBal) !== bal
        && Math.abs(computeEntry(fills).qty - bal) > 0.01 * bal
      // pos-tag-20261023: an instant placeholder (bucket price, no fills) whose
      // first backfill ran before the buy was indexed kept the guessed entry
      // forever. Retry a few times until real fills replace it.
      const guess = !fills.length && rec.source === 'instant' && (instTries.get(mint) || 0) < 5
      if (guess && !never && Date.now() >= (bfNext.get(mint) || 0)) instTries.set(mint, (instTries.get(mint) || 0) + 1)
      if (never || mismatch || guess) queueBackfill(mint)
    }
  }
  if (confirm) refreshSoon(CONFIRM_MS)
}

const instTries = new Map()

function queueBackfill(mint) {
  if (!walletPk || bfQueue.includes(mint)) return
  if (Date.now() < (bfNext.get(mint) || 0)) return
  bfNext.set(mint, Date.now() + 20000)
  bfQueue.push(mint)
  pumpBackfill()
}

async function pumpBackfill() {
  if (bfRunning) return
  bfRunning = true
  try {
    while (bfQueue.length) {
      const mint = bfQueue.shift()
      try { await backfillOne(mint) }
      catch (e) {
        bfNext.set(mint, Date.now() + 60000)
        console.warn('[positions] backfill failed', String(mint).slice(0, 8), e?.message || e)
      }
    }
  } finally { bfRunning = false }
}

async function backfillOne(mint) {
  const pk = walletPk, w = wallet58
  if (!pk) return
  const res = await getMintFillsSinceFlat(pk, mint)
  if (w !== wallet58) return
  const now = Date.now()
  const rec = readRec(mint)
  const t = lastTotals && lastTotals.get(mint)
  if (!rec && !(t && t.nonzero)) return
  const base = rec || {
    id: newId(), wallet: w, token: mint, tokenAmount: t.ui, fills: [],
    timestamp: now, zeroReads: 0, pendingUntil: 0, source: 'chain', entrySolPrice: 0,
  }
  const old = Array.isArray(base.fills) ? base.fills : []
  let fills = old
  if (res.fills.length) {
    const chain = new Set(res.fills.map((f) => sig16(f.sig)))
    const recent = old.filter((f) => f && f.live && !chain.has(sig16(f.sig)) && now - num(f.ts) < LIVE_KEEP_MS)
    fills = res.fills.concat(recent)
  }
  let priceSol = computeEntry(fills).priceSol
  let detected = false
  if (!(priceSol > 0)) {
    priceSol = priceSolOf(base)
    if (priceSol > 0) detected = !!base.detected
    else {
      try { priceSol = num(cfg.getFallbackPriceSol(mint)) } catch { priceSol = 0 }
      detected = priceSol > 0
    }
  }
  if (!(priceSol > 0)) {
    // Nothing to place a tag at yet. Back off harder after a few misses.
    const miss = (bfMiss.get(mint) || 0) + 1
    bfMiss.set(mint, miss)
    bfNext.set(mint, now + (miss >= 3 ? 30 * 60000 : 30000))
    if (rec) writeRec(mint, { ...rec, backfilledAt: now, backfillBal: num(rec.tokenAmount) })
    return
  }
  bfMiss.delete(mint)
  writeRec(mint, {
    ...base, fills, entryPriceSol: priceSol, detected,
    backfilledAt: now, backfillBal: num(base.tokenAmount),
    source: res.fills.length ? 'chain' : base.source,
  })
}

export async function refreshNow() {
  if (!walletPk) return
  if (inFlight) { again = true; return }
  inFlight = true
  const pk = walletPk, w = wallet58
  try {
    let totals
    try { totals = await getWalletMintTotals(pk) }
    catch (e) { console.warn('[positions] balance read failed - keeping last state:', e?.message || e); return }
    if (w !== wallet58) return
    lastTotals = totals
    applyTotals(totals)
  } finally {
    inFlight = false
    if (again) { again = false; refreshSoon(300) }
  }
}

export function refreshSoon(ms = 1500) {
  if (!walletPk) return
  const at = Date.now() + ms
  if (soonTimer && soonAt <= at) return
  if (soonTimer) clearTimeout(soonTimer)
  soonAt = at
  soonTimer = setTimeout(() => { soonTimer = 0; soonAt = 0; refreshNow() }, ms)
}

export function want(mint) {
  if (!mint) return
  wanted.add(mint)
  refreshSoon(300)
}

export function configure(c) { cfg = { ...cfg, ...(c || {}) } }

// Server wallet_activity: a fill (deduped by signature) + a balance refresh.
// It never adds or subtracts size itself.
export function onWalletActivity(msg) {
  if (!msg || !msg.mint || (msg.side !== 'buy' && msg.side !== 'sell')) return
  const tok = num(msg.tokens), sol = num(msg.sol)
  if (!(tok > 0)) return
  const sig = msg.sig ? sig16(msg.sig) : `${msg.side}:${tok}:${sol}`
  pushFill(msg.mint, { sig, ts: Date.now(), side: msg.side, tok, sol, live: true })
  wanted.add(msg.mint)
  try { window.dispatchEvent(new CustomEvent('solcheddar-wallet-position-updated', { detail: { mint: msg.mint } })) } catch {}
  refreshSoon(1500)
}

// A fill from another source (Helius parse of our own tx).
export function addFill(mint, fill) {
  if (!mint) return
  pushFill(mint, { live: true, ts: Date.now(), ...fill })
  wanted.add(mint)
  refreshSoon(1500)
}

function addListeners() {
  if (listenersOn || typeof window === 'undefined') return
  listenersOn = true
  window.addEventListener('online', () => refreshSoon(1000))
  window.addEventListener('solcheddar-ws-open', () => refreshSoon(1000))
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshSoon(500) })
}

export function setWallet(pk) {
  let w = '', obj = null
  try {
    if (typeof pk === 'string' && pk) { w = pk; obj = new PublicKey(pk) }
    else if (pk && typeof pk.toBase58 === 'function') { w = pk.toBase58(); obj = pk }
  } catch { w = ''; obj = null }
  if (w === wallet58 && !!obj === !!walletPk) return
  wallet58 = w
  walletPk = obj
  lastTotals = null
  bfQueue.length = 0
  bfNext.clear()
  bfMiss.clear()
  if (w) positionStore.setWallet(w)
  if (pollTimer) { clearInterval(pollTimer); pollTimer = 0 }
  if (!walletPk) return
  addListeners()
  pollTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return
    refreshNow()
  }, POLL_MS)
  refreshSoon(500)
}